import {AfterViewInit, Component, ElementRef, inject, OnDestroy, signal, viewChild} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatDialog} from '@angular/material/dialog'
import {MatFormField, MatLabel} from '@angular/material/form-field'
import {MatOption, MatSelect} from '@angular/material/select'
import QrScanner from 'qr-scanner'
import {EMPTY} from 'rxjs'
import {catchError} from 'rxjs/operators'
import {LOCAL_STORAGE} from '../0-application/app'
import {QR_SCANNER_FACTORY} from '../0-application/qr-scanner.provider'
import {IdListDialogComponent} from '../common/id-list-dialog/id-list-dialog.component'
import {WaitDialogComponent} from '../common/wait-dialog/wait-dialog.component'
import {DataService} from '../services/data.service'
import {FakeScanner} from '../stubs/fake.scanner'
import Camera = QrScanner.Camera
import ScanResult = QrScanner.ScanResult

@Component({
  selector: 'spb-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss'],
  imports: [
    MatFormField,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    MatLabel
  ]
})
export class ScannerComponent implements AfterViewInit, OnDestroy {
  public video = viewChild.required<ElementRef>('video')
  public cameraList = signal<Camera[]>([])
  public selectedCamera: FormControl = new FormControl<string>('')

  /**
   * Add a fake to avoid 1M ifs.
   */
  private qrScanner: QrScanner = new FakeScanner(null as any) as any


  private readonly qrFactory = inject(QR_SCANNER_FACTORY)
  private readonly storage = inject<Storage>(LOCAL_STORAGE)
  private readonly dataService = inject(DataService)
  private readonly dialog = inject(MatDialog)

  public ngOnDestroy() {
    this.stop()
  }

  public ngAfterViewInit() {
    const videoEl = this.video().nativeElement

    this.selectedCamera.valueChanges.subscribe(cameraId => {
      this.changeCamera(cameraId)
    })

    const savedCamera: string | null = this.storage.getItem('camera')
    this.selectedCamera.setValue(savedCamera)

    const scannerOptions = {
      returnDetailedScanResult: true,
      preferredCamera: savedCamera,
      maxScansPerSecond: 10
    }
    this.qrScanner = this.qrFactory.getScanner(videoEl, this.scannerCallback, scannerOptions)
    this.start().then()
  }

  public scannerCallback = (result: ScanResult) => {

    /**
     * Validates the scanned QR code before processing further actions.
     * If the QR code passes the check, it proceeds with identification,
     * opens a waiting dialog, and stops further scanning.
     */
    this.stop()
    this.identify(result.data)
    this.dialog.open(WaitDialogComponent)

  }

  public async start() {
    await this.qrScanner.start()
    this.cameraList.set(await this.qrFactory.listCameras(true))
  }

  public stop(): void {
    this.qrScanner.stop()
  }

  public changeCamera(cameraId: string): void {
    this.qrScanner.setCamera(cameraId).then()
    this.storage.setItem('camera', cameraId)
  }

  private identify(scanResult: string): void {
    this.dataService.checkId(scanResult).pipe(
      catchError(() => {
        this.dialog.closeAll()
        this.dataService.userData.set({
          timeStamp: Date.now(),
          errorCode: 'Felaktig QR kod',
          details: 'QR-koden är ogiltig',
          title: 'Ej legitimerad',
          sub: ''
        })

        this.openDialog()
        return EMPTY
      })
    ).subscribe(res => {
      /**
       * Close analyze ref
       */
      this.dialog.closeAll()

      if ('user' in res) {
        this.dataService.userData.set({...res, title: 'Legitimerad'})
      } else {
        this.dataService.userData.set({...res, title: 'Ej legitimerad'})
      }
      this.openDialog()
    })
  }

  private openDialog(): void {
    this.dialog.open(IdListDialogComponent)
      .afterClosed()
      .subscribe(() => this.start())
  }
}
